import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { X, Plus, CheckSquare, Users, Building2, UserCircle, Copy, Trash2 } from "lucide-react";
import { Input } from "@/modern/components/ui/input";
import { Button } from "@/modern/components/ui/button";
import { Textarea } from "@/modern/components/ui/textarea";
import { Badge } from "@/modern/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/modern/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/modern/components/ui/tabs";
import { Combobox } from "@/modern/components/ui/combobox";
import { PhoneInput } from "@/modern/components/phone-input";
import { contactSchema, ContactFormData, ILinkedEntity } from "@/react/schema/contactSchema";
import { useEntityContactContext } from "@/react/components/entityViews/contacts/components/context/EntityContactContext";
import { LinkEntityEditor } from "@/react/components/LinkEntityEditor";
import { MoreMenu, MenuItem } from "@/modern/components/more-menu";
import { Contact } from "@/bryntum-lib/models/Contact";
import { TagStore } from "@/react/hooks/entityHooks/useSwivuContact";
import { getEntityIcon } from "@/other/utils/utils.jsx";
import { cn } from "@/modern/utils";
import { EntityRecord } from "@/types/entity";

interface ContactEditorProps {
  contact?: Contact | null; // If editing existing contact
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ContactEditor: React.FC<ContactEditorProps> = ({ contact, open, onOpenChange }) => {
  const { createContact, updateContact, loading, getTagStores, copyContactToClipboard, handleDeleteContact, entityRecord } = useEntityContactContext();

  const isEditing = !!contact;
  const [linkEditorOpen, setLinkEditorOpen] = useState(false);
  const [linkedEntities, setLinkedEntities] = useState<ILinkedEntity[]>([]);
  const [initialLinkedEntities, setInitialLinkedEntities] = useState<ILinkedEntity[]>([]);

  // Get tag stores
  const { statusStore, categoryStore, tagsStore } = useMemo(() => getTagStores(), [getTagStores]);

  // Convert to combobox options
  const statusOptions = useMemo(() => statusStore.map((tag: TagStore) => ({ label: tag.tagValue, value: tag.id })), [statusStore]);
  const categoryOptions = useMemo(() => categoryStore.map((tag: TagStore) => ({ label: tag.tagValue, value: tag.id })), [categoryStore]);
  const tagOptions = useMemo(() => tagsStore.map((tag: TagStore) => ({ label: tag.tagValue, value: tag.id })), [tagsStore]);

  // Initialize form
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    mode: "onChange",
    defaultValues: {
      name: contact?.name || "",
      phone: contact?.phone || "",
      email: contact?.email || "",
      address: contact?.address || "",
      statusId: contact?.statusId || null,
      categoryId: contact?.categoryId || null,
      tagsId: contact?.tagsId || [],
      noteText: contact?.noteText || ""
    }
  });

  // Helper function to create linked entity from record
  const createLinkedEntityFromRecord = useCallback((record: EntityRecord): ILinkedEntity | null => {
    if (!record) return null;

    const entityTypeKey = record.entityType; // 'taskId', 'resourceId', or 'clientId'

    // Map entityType to display name and store keys
    const entityTypeMap: Record<string, { type: string; relationKey: string; storeKey: string }> = {
      taskId: {
        type: "Task",
        relationKey: "taskId",
        storeKey: "taskContactStore"
      },
      resourceId: {
        type: "Resource",
        relationKey: "resourceId",
        storeKey: "resourceContactStore"
      },
      clientId: {
        type: "Client",
        relationKey: "clientId",
        storeKey: "clientContactStore"
      }
    };

    const config = entityTypeMap[entityTypeKey];
    if (!config) {
      console.warn(`Unknown entity type: ${entityTypeKey}`);
      return null;
    }

    return {
      id: `${config.type}-${record.id}`,
      name: record.name || "",
      type: config.type,
      entityRecord: record,
      entityContactStoreKey: config.storeKey,
      entityRelationKey: config.relationKey
    };
  }, []);

  // Reset form and state when dialog closes
  useEffect(() => {
    if (!open) {
      // Reset form to default values
      reset({
        name: "",
        phone: "",
        email: "",
        address: "",
        statusId: null,
        categoryId: null,
        tagsId: [],
        noteText: ""
      });
      // Clear linked entities
      setLinkedEntities([]);
      setInitialLinkedEntities([]);
    }
  }, [open, reset]);

  // Load linked entities for existing contact or auto-link current record for new contact
  useEffect(() => {
    if (open && contact) {
      // Filter out entities with null keys (type from Contact model has nullable fields)
      const entities = (contact.entities || []).filter(
        (entity: { entityContactStoreKey: string | null; entityRelationKey: string | null }) => entity.entityContactStoreKey && entity.entityRelationKey
      ) as ILinkedEntity[];
      setLinkedEntities(entities);
      setInitialLinkedEntities(entities);
      // Reset form with contact data
      reset({
        name: contact.name || "",
        phone: contact.phone || "",
        email: contact.email || "",
        address: contact.address || "",
        statusId: contact.statusId || null,
        categoryId: contact.categoryId || null,
        tagsId: contact.tagsId || [],
        noteText: contact.noteText || ""
      });
    } else if (open && !contact && entityRecord) {
      // Auto-link current record for new contact
      const linkedEntity = createLinkedEntityFromRecord(entityRecord);
      if (linkedEntity) {
        setLinkedEntities([linkedEntity]);
        setInitialLinkedEntities([linkedEntity]);
      }
    }
  }, [open, contact, entityRecord, createLinkedEntityFromRecord, reset]);

  // Handle link entity save
  const handleLinkSave = useCallback((newLinkedData: ILinkedEntity[]) => {
    setLinkedEntities(newLinkedData);
    setLinkEditorOpen(false);
  }, []);

  // Remove linked entity
  const removeLinkedEntity = useCallback((entityId: string) => {
    setLinkedEntities((prev) => prev.filter((e) => e.id !== entityId));
  }, []);

  // Group linked entities by type
  const groupedEntities = useMemo(() => {
    return {
      Task: linkedEntities.filter((e) => e.type === "Task"),
      Resource: linkedEntities.filter((e) => e.type === "Resource"),
      Client: linkedEntities.filter((e) => e.type === "Client"),
      User: linkedEntities.filter((e) => e.type === "User")
    };
  }, [linkedEntities]);

  // Check if entity is currently opened in entity manager
  const isCurrentlyOpenedEntity = useCallback(
    (entity: ILinkedEntity): boolean => {
      if (!entityRecord) return false;
      return entity.type === entityRecord.type && entity.entityRecord?.id === entityRecord.id;
    },
    [entityRecord]
  );

  // Render entity list for a tab
  const renderEntityTab = useCallback(
    (entities: ILinkedEntity[], emptyMessage: string, type: string) => {
      if (entities.length === 0) {
        return (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="rounded-full bg-muted/90 p-4 mb-3">{getEntityIcon(type)}</div>
            <p className="text-base text-muted-foreground">{emptyMessage}</p>
          </div>
        );
      }

      return (
        <>
          {entities.map((entity) => (
            <div key={entity.id} className={cn("group flex items-center gap-3 p-3 border-b")}>
              <div className="flex-1 min-w-0 flex items-center gap-2">
                <p className="text-base m-0 font-medium truncate">{entity?.name || "N/A"}</p>
                {isCurrentlyOpenedEntity(entity) && (
                  <Badge variant="secondary" className="text-[10px] px-1.5 py-0 shrink-0">
                    Current
                  </Badge>
                )}
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className={cn("h-7 w-7 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity", "hover:bg-destructive/10 hover:text-destructive")}
                onClick={() => removeLinkedEntity(entity.id)}
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </>
      );
    },
    [removeLinkedEntity, isCurrentlyOpenedEntity]
  );

  // Handle form submission
  const onSubmit = async (data: ContactFormData) => {
    // Prevent multiple submissions
    if (loading) {
      return;
    }

    try {
      if (isEditing) {
        // Calculate changes in linked entities
        const added = linkedEntities.filter((e) => !initialLinkedEntities.some((ie) => ie.id === e.id));
        const removed = initialLinkedEntities.filter((ie) => !linkedEntities.some((e) => e.id === ie.id));

        await updateContact(contact.id, data, { added, removed });
      } else {
        // Creating new contact - filter out invalid linked entities
        const validLinkedEntities = linkedEntities.filter((entity) => entity.entityContactStoreKey && entity.entityRelationKey && entity.entityRecord?.id);
        await createContact(data, validLinkedEntities);
      }
      onOpenChange(false);
    } catch (error) {
      console.error("Failed to save contact:", error);
    }
  };

  // Menu items for MoreMenu in header
  const editorMenuItems: MenuItem[] = useMemo(() => {
    if (!isEditing || !contact) return [];

    return [
      {
        itemVariant: "button",
        menuText: "Copy Contact",
        Component: ({ onFinish }) => (
          <div
            className="flex items-center w-full cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              copyContactToClipboard(contact);
              onFinish?.();
            }}
          >
            <Copy className="h-4 w-4 mr-2" />
            <span>Copy Link</span>
          </div>
        )
      },
      {
        itemVariant: "button" as const,
        menuText: "Delete",
        dropdownMenuItemProps: { className: "text-destructive" },
        Component: ({ onFinish }: { onFinish?: () => void }) => (
          <div
            className="flex items-center w-full cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteContact(contact);
              onOpenChange(false);
              onFinish?.();
            }}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            <span>Delete Contact</span>
          </div>
        )
      }
    ];
  }, [isEditing, contact, copyContactToClipboard, handleDeleteContact, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent closeButtonClassName="top-3" className="max-w-6xl max-h-[90vh] p-0 flex flex-col gap-0 dark:bg-background">
        <DialogHeader className="px-6 py-4 border-b shrink-0 relative space-y-0">
          <DialogTitle className="text-lg font-normal">{isEditing ? "Edit Contact" : "Create New Contact"}</DialogTitle>
          {isEditing && editorMenuItems.length > 0 && (
            <div className="absolute right-14 top-1/2 -translate-y-1/2">
              <MoreMenu menuItems={editorMenuItems} triggerButtonClassName="bg-transparent border-none hover:bg-transparent size-6 p-0" />
            </div>
          )}
        </DialogHeader>
        <div className="overflow-y-auto px-6 py-6">
          <form className="space-y-6">
            {/* Two column grid for main fields */}
            <div className="grid grid-cols-2 gap-4">
              {/* Name */}
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Name <span className="text-destructive">*</span>
                </label>
                <Controller name="name" control={control} render={({ field }) => <Input {...field} variant="outline" placeholder="Enter name" className="h-10" />} />
                {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
              </div>

              {/* Email */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Email</label>
                <Controller name="email" control={control} render={({ field }) => <Input {...field} variant="outline" type="email" placeholder="email@example.com" className="h-10" />} />
                {errors.email && <p className="text-xs text-destructive">{errors.email.message}</p>}
              </div>

              {/* Phone */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Phone</label>
                <Controller name="phone" control={control} render={({ field }) => <PhoneInput {...field} defaultCountry="US" placeholder="Enter phone number" className="h-10" />} />
              </div>

              {/* Status */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Status</label>
                <Controller
                  name="statusId"
                  control={control}
                  render={({ field }) => <Combobox options={statusOptions} value={field.value ?? undefined} onValueChange={field.onChange} placeholder="Select status..." clearable />}
                />
              </div>

              {/* Category */}
              <div className="space-y-2 col-span-full">
                <label className="text-sm font-medium">Category</label>
                <Controller
                  name="categoryId"
                  control={control}
                  render={({ field }) => <Combobox options={categoryOptions} value={field.value ?? undefined} onValueChange={field.onChange} placeholder="Select category..." clearable />}
                />
              </div>

              {/* Address */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Address</label>
                <Controller name="address" control={control} render={({ field }) => <Textarea {...field} rows={3} placeholder="Enter address" className="resize-none" />} />
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Note</label>
                <Controller name="noteText" control={control} render={({ field }) => <Textarea {...field} rows={3} placeholder="Add notes..." className="resize-none" />} />
              </div>

              {/* Tags */}
              <div className="col-span-full space-y-2">
                <label className="text-sm font-medium">Tags</label>
                <Controller
                  name="tagsId"
                  control={control}
                  render={({ field }) => (
                    <Combobox showBadges={true} maxDisplay={4} options={tagOptions} value={field.value || []} onValueChange={field.onChange} placeholder="Select tags..." multiSelect />
                  )}
                />
              </div>
            </div>

            {/* Linked Entities - Modern Design */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium">Linked Entities</label>
                  {linkedEntities.length > 0 && (
                    <Badge variant="secondary" className="h-5 px-2 text-xs font-normal">
                      {linkedEntities.length}
                    </Badge>
                  )}
                </div>
                <Button type="button" variant="ghost" size="icon" onClick={() => setLinkEditorOpen(true)} className="h-8 w-8">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>

              <div className="border rounded-md overflow-hidden">
                <Tabs defaultValue="tasks" className="w-full">
                  <TabsList className="w-full">
                    <TabsTrigger value="tasks" className="flex-1 text-base gap-2">
                      <CheckSquare className="h-3.5 w-3.5" />
                      Tasks
                      {groupedEntities.Task.length > 0 && (
                        <Badge variant="secondary" className="ml-1 h-5 min-w-5 px-1.5 text-base font-medium">
                          {groupedEntities.Task.length}
                        </Badge>
                      )}
                    </TabsTrigger>
                    <TabsTrigger value="resources" className="flex-1 text-base gap-2 border-l">
                      <Users className="h-3.5 w-3.5" />
                      Resources
                      {groupedEntities.Resource.length > 0 && (
                        <Badge variant="secondary" className="ml-1 h-5 min-w-5 px-1.5 text-base font-medium">
                          {groupedEntities.Resource.length}
                        </Badge>
                      )}
                    </TabsTrigger>
                    <TabsTrigger value="clients" className="flex-1 text-base gap-2 border-l">
                      <Building2 className="h-3.5 w-3.5" />
                      Clients
                      {groupedEntities.Client.length > 0 && (
                        <Badge variant="secondary" className="ml-1 h-5 min-w-5 px-1.5 text-base font-medium">
                          {groupedEntities.Client.length}
                        </Badge>
                      )}
                    </TabsTrigger>
                    <TabsTrigger value="users" className="flex-1 text-base gap-2 border-l">
                      <UserCircle className="h-3.5 w-3.5" />
                      Users
                      {groupedEntities.User.length > 0 && (
                        <Badge variant="secondary" className="ml-1 h-5 min-w-5 px-1.5 text-base font-medium">
                          {groupedEntities.User.length}
                        </Badge>
                      )}
                    </TabsTrigger>
                  </TabsList>

                  <div className="px-1 overflow-y-auto">
                    <TabsContent value="tasks" className="mt-0">
                      {renderEntityTab(groupedEntities.Task, "No tasks linked", "Task")}
                    </TabsContent>

                    <TabsContent value="resources" className="mt-0">
                      {renderEntityTab(groupedEntities.Resource, "No resources linked", "Resource")}
                    </TabsContent>

                    <TabsContent value="clients" className="mt-0">
                      {renderEntityTab(groupedEntities.Client, "No clients linked", "Client")}
                    </TabsContent>

                    <TabsContent value="users" className="mt-0">
                      {renderEntityTab(groupedEntities.User, "No board users linked", "User")}
                    </TabsContent>
                  </div>
                </Tabs>
              </div>
            </div>
          </form>
        </div>
        {/* Actions */}
        <div className="flex justify-end  px-6 py-6 gap-3 border-t">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={() => handleSubmit(onSubmit)()} disabled={loading}>
            {loading ? "Saving..." : isEditing ? "Save Changes" : "Create Contact"}
          </Button>
        </div>
      </DialogContent>

      {/* Link Entity Editor Dialog */}
      <LinkEntityEditor initialLinkedData={linkedEntities} open={linkEditorOpen} onOpenChange={setLinkEditorOpen} onSave={handleLinkSave} />
    </Dialog>
  );
};
